/**
 * Phase 2L-A40 — "Today's Attention" home health summary card.
 *
 * Single calm card shown at the top of the Admin / Lab Tech home
 * dashboard. Pulls counter data from `GET /api/admin/home-health-summary`
 * which is role-aware: admins see 7 counters (samples + reports +
 * customer follow-ups + payments + proposals + Stripe webhook
 * failures), lab tech see only the 3 lab-focused counters.
 *
 * Refresh policy: load on mount + manual "Refresh" button. No
 * auto-polling — staff should not feel the page is twitching.
 *
 * Demo/test data is excluded server-side (no toggle on this widget).
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ActivitySquare, ArrowRight, RefreshCw, Loader2, AlertTriangle,
} from "lucide-react";
import api from "@/lib/api";

const FRIENDLY_EMPTY = "All clear";

export default function HomeHealthSummary() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const { data: resp } = await api.get("/admin/home-health-summary");
      setData(resp);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load summary");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const counters = data?.counters || [];
  const total = data?.total_needs_action ?? 0;
  const generatedAt = data?.generated_at
    ? new Date(data.generated_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <section
      data-testid="home-health-summary"
      className="rounded-3xl bg-white border border-slate-200 p-6 sm:p-7"
    >
      <header className="flex items-start justify-between gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700">
            <ActivitySquare className="w-3.5 h-3.5" />
            Today's attention
          </div>
          <h2 className="font-display text-xl sm:text-2xl font-semibold text-slate-900 mt-1.5">
            {loading
              ? "Loading…"
              : total === 0
                ? FRIENDLY_EMPTY
                : `${total} item${total === 1 ? "" : "s"} to action`}
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Demo / test data excluded
            {generatedAt && <> · Updated {generatedAt}</>}
          </p>
        </div>
        <button
          type="button"
          data-testid="home-health-summary-refresh"
          onClick={() => load({ silent: true })}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-60"
        >
          {refreshing
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : <RefreshCw className="w-3.5 h-3.5" />}
          Refresh
        </button>
      </header>

      {loading ? (
        <div className="py-8 text-center text-slate-400 text-sm">
          <Loader2 className="w-5 h-5 animate-spin inline-block" />
        </div>
      ) : error ? (
        <div className="py-6 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-2xl px-4">
          {error}
        </div>
      ) : (
        <ul data-testid="home-health-summary-counters" className="divide-y divide-slate-100">
          {counters.map((c) => (
            <CounterRow key={c.key} counter={c} />
          ))}
        </ul>
      )}
    </section>
  );
}

function CounterRow({ counter }) {
  const {
    key, label, count, highlight_count, highlight_label, link,
  } = counter;
  const isZero = (count || 0) === 0;
  const hasHighlight = (highlight_count || 0) > 0;

  const inner = (
    <div className="flex items-center justify-between py-3.5">
      <div className="min-w-0">
        <div className={`font-medium text-sm ${isZero ? "text-slate-500" : "text-slate-900"}`}>
          {label}
        </div>
        {hasHighlight && highlight_label && (
          <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
            <AlertTriangle className="w-3 h-3" />
            {highlight_count} {highlight_label}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span
          data-testid={`home-health-count-${key}`}
          className={`font-display font-bold tabular-nums ${isZero ? "text-3xl text-slate-300" : "text-3xl text-slate-900"}`}
        >
          {count}
        </span>
        {link && (
          <ArrowRight className={`w-4 h-4 ${isZero ? "text-slate-300" : "text-cyan-600"}`} />
        )}
      </div>
    </div>
  );

  // Non-clickable when the linked page doesn't add value (count is zero
  // AND there's no highlight). Keeps the card calm — only the rows that
  // need action stay interactive.
  if (!link || (isZero && !hasHighlight)) {
    return (
      <li data-testid={`home-health-row-${key}`} className="opacity-90">
        {inner}
      </li>
    );
  }
  return (
    <li>
      <Link
        data-testid={`home-health-row-${key}`}
        to={link}
        className="block hover:bg-slate-50 -mx-2 px-2 rounded-xl transition-colors"
      >
        {inner}
      </Link>
    </li>
  );
}
